import type { IntervalResult, TrackingInterval } from "../contracts";
import type { RecoveryImage } from "../recovery/contracts";
import { recoveryAbortError } from "../recovery/coordinator";

interface PendingRequest<T> {
  signal: AbortSignal;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

export interface ExtractionRequest extends PendingRequest<RecoveryImage[]> {
  base: IntervalResult;
  targetTime: number;
}

export interface RefinementRequest extends PendingRequest<IntervalResult> {
  parent: TrackingInterval;
  interval: TrackingInterval;
}

const distance = (interval: TrackingInterval, time: number) =>
  Math.max(interval.start.time - time, time - interval.end.time, 0);

/** Pending work only; the runtime owns admission, active cancellation and shared resources. */
export class SessionRecoveryQueue {
  private extractions: ExtractionRequest[] = [];
  private refinements: RefinementRequest[] = [];

  constructor(private readonly onQueued: () => void) {}

  extract(base: IntervalResult, targetTime: number, signal: AbortSignal): Promise<RecoveryImage[]> {
    return this.enqueue(signal, (request) => this.extractions.push({ ...request, base, targetTime }));
  }

  refine(
    parent: TrackingInterval,
    children: readonly TrackingInterval[],
    signal: AbortSignal,
  ): Promise<IntervalResult[]> {
    return Promise.all(
      children.map((interval) =>
        this.enqueue<IntervalResult>(signal, (request) => this.refinements.push({ ...request, parent, interval })),
      ),
    );
  }

  discardCancelled(): void {
    this.extractions = this.extractions.filter((request) => !request.signal.aborted);
    this.refinements = this.refinements.filter((request) => !request.signal.aborted);
  }

  peekExtraction(playhead: number): ExtractionRequest | undefined {
    this.extractions.sort((a, b) => distance(a.base.interval, playhead) - distance(b.base.interval, playhead));
    return this.extractions[0];
  }

  takeExtraction(): ExtractionRequest | undefined {
    return this.extractions.shift();
  }

  takeRefinement(playhead: number, admit: boolean): RefinementRequest | undefined {
    this.refinements.sort((a, b) => distance(a.parent, playhead) - distance(b.parent, playhead));
    return admit ? this.refinements.shift() : undefined;
  }

  rejectAll(error: unknown): void {
    this.extractions.splice(0).forEach((request) => request.reject(error));
    this.refinements.splice(0).forEach((request) => request.reject(error));
  }

  private enqueue<T>(signal: AbortSignal, append: (request: PendingRequest<T>) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) return reject(recoveryAbortError());
      const abort = () => reject(signal.reason ?? recoveryAbortError());
      signal.addEventListener("abort", abort, { once: true });
      append({
        signal,
        resolve: (value) => {
          signal.removeEventListener("abort", abort);
          resolve(value);
        },
        reject: (error) => {
          signal.removeEventListener("abort", abort);
          reject(error);
        },
      });
      this.onQueued();
    });
  }
}
